import React, { useState } from 'react';

import { Button, Chip, Stack } from '@mui/material';

import { humanise, memberLabel } from './format';
import { Picker, Reveal, Row, TextInput } from './settingsKit';
import type { Block, EnvelopeColumn, LensDocument } from './types';
import type { BoardEditor } from './useBoardEdit';

type Tone = 'good' | 'warning' | 'critical' | 'info';
type ToneRule = { above?: number; below?: number; tone: Tone };
type Group = { label: string; columns: Array<string> };

const TONES: Array<[Tone, string]> = [
  ['good', 'Good'],
  ['warning', 'Warning'],
  ['critical', 'Critical'],
  ['info', 'Info'],
];

const CHIP = { fontSize: 12.5, height: 'auto', maxWidth: '100%', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } } as const;

const set = (editor: BoardEditor, block: Block, patch: Record<string, unknown>) => editor.apply({ op: 'set', id: block.id, patch });

export function FollowsFilters({ block, lens, editor, labelOf }: { block: Block; lens: LensDocument; editor: BoardEditor; labelOf: (column: string) => string }) {
  if (!lens.filters.length) return null;
  const ignored = Array.isArray(block.ignore) ? (block.ignore as Array<string>) : [];
  return (
    <Row keywords={['ignore', 'filters']} label="Follows Lens filters" set={ignored.length > 0} stacked>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {lens.filters.map((filter) => {
          const off = ignored.includes(filter.column);
          const next = off ? ignored.filter((column) => column !== filter.column) : [...ignored, filter.column];
          return (
            <Chip
              aria-pressed={!off}
              clickable
              color={off ? 'default' : 'primary'}
              disabled={editor.pending}
              key={filter.column}
              label={filter.label ?? labelOf(filter.column)}
              onClick={() => set(editor, block, { ignore: next.length ? next : null })}
              size="small"
              sx={{ fontSize: 12.5, ...(off ? { textDecoration: 'line-through' } : {}) }}
              variant={off ? 'outlined' : 'filled'}
            />
          );
        })}
      </Stack>
    </Row>
  );
}

function switchChoices(lens: LensDocument, name: string): Array<[string, string]> {
  const param = lens.params[name] as Record<string, unknown> | undefined;
  const list = (param?.measures ?? param?.columns ?? param?.options ?? []) as Array<string>;
  return list.map((value) => [value, humanise(value.slice(value.lastIndexOf('.') + 1))]);
}

export function ShownWhen({ block, lens, editor }: { block: Block; lens: LensDocument; editor: BoardEditor }) {
  const names = Object.keys(lens.params).filter((name) => switchChoices(lens, name).length);
  const [param, setParam] = useState('');
  const [value, setValue] = useState('');
  if (!names.length) return null;
  const when = (block.when ?? {}) as Record<string, Array<string>>;
  const labelOf = (name: string) => String((lens.params[name] as { label?: string }).label ?? humanise(name));
  return (
    <Row keywords={['when', 'switch', 'visible']} label="Shown when" set={Object.keys(when).length > 0} stacked>
      <Stack spacing={0.75} sx={{ width: '100%' }}>
        {Object.keys(when).length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {Object.entries(when).map(([name, values]) => (
              <Chip
                key={name}
                label={`${labelOf(name.replace(/^\$/, ''))} is ${values.map((entry) => humanise(entry.slice(entry.lastIndexOf('.') + 1))).join(' or ')}`}
                onDelete={editor.pending ? undefined : () => set(editor, block, { when: Object.keys(when).length > 1 ? { [name]: null } : null })}
                size="small"
                sx={CHIP}
              />
            ))}
          </Stack>
        ) : null}
        <Stack alignItems="center" direction="row" spacing={0.75}>
          <Picker choices={names.map((name) => [name, labelOf(name)])} label="Switch" onChange={(next) => {
              setParam(next);
              setValue('');
            }} placeholder="Switch…" value={param} />
          <Picker choices={param ? switchChoices(lens, param) : []} label="Is" onChange={setValue} placeholder="is…" value={value} />
          <Button
            disabled={editor.pending || !param || !value}
            onClick={() => {
              set(editor, block, { when: { [param]: [...new Set([...(when[param] ?? []), value])] } });
              setParam('');
              setValue('');
            }}
            size="small"
            sx={{ flexShrink: 0, minWidth: 0 }}
            variant="text"
          >
            Add
          </Button>
        </Stack>
      </Stack>
    </Row>
  );
}

export function ReadsQueries({ block, lens, editor }: { block: Block; lens: LensDocument; editor: BoardEditor }) {
  const names = Object.keys(lens.queries ?? {});
  if (!names.length) return null;
  const reads = Array.isArray(block.reads) ? (block.reads as Array<string>) : [];
  return (
    <Row label="Reads queries" set={reads.length > 0} stacked>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {names.map((name) => {
          const on = reads.includes(name);
          const next = on ? reads.filter((entry) => entry !== name) : [...reads, name];
          return (
            <Chip aria-pressed={on} clickable color={on ? 'primary' : 'default'} disabled={editor.pending} key={name} label={name} onClick={() => set(editor, block, { reads: next.length ? next : null })} size="small" sx={{ fontSize: 12.5 }} variant={on ? 'filled' : 'outlined'} />
          );
        })}
      </Stack>
    </Row>
  );
}

function ruleText(rule: ToneRule) {
  const tone = TONES.find(([id]) => id === rule.tone)?.[1] ?? rule.tone;
  if (rule.below !== undefined) return `below ${rule.below} → ${tone}`;
  if (rule.above !== undefined) return `above ${rule.above} → ${tone}`;
  return `otherwise → ${tone}`;
}

export function ValuePills({ block, editor, columns, members }: { block: Block; editor: BoardEditor; columns: Array<EnvelopeColumn>; members: (column: string) => Array<string> }) {
  const tones = (block.tones ?? {}) as Record<string, Record<string, Tone> | Array<ToneRule>>;
  const [column, setColumn] = useState('');
  const [when, setWhen] = useState('below');
  const [at, setAt] = useState('');
  const [tone, setTone] = useState<Tone>('good');
  const chosen = columns.find((entry) => entry.name === column);
  const numeric = chosen?.role === 'measure';
  const labelOf = (name: string) => columns.find((entry) => entry.name === name)?.label ?? humanise(name);
  const entries = Object.entries(tones).flatMap(([name, rule]) =>
    Array.isArray(rule)
      ? rule.map((entry, index) => ({ key: `${name}#${index}`, text: `${labelOf(name)} ${ruleText(entry)}`, remove: () => set(editor, block, { tones: { [name]: rule.length > 1 ? rule.filter((_, other) => other !== index) : null } }) }))
      : Object.entries(rule).map(([member, entry]) => ({ key: `${name}:${member}`, text: `${labelOf(name)} ${memberLabel(member)} → ${TONES.find(([id]) => id === entry)?.[1] ?? entry}`, remove: () => set(editor, block, { tones: { [name]: Object.keys(rule).length > 1 ? { [member]: null } : null } }) })),
  );
  const add = () => {
    const current = tones[column];
    if (numeric) {
      const rules = Array.isArray(current) ? current : [];
      const rule: ToneRule = when === 'otherwise' ? { tone } : { [when]: Number(at), tone };
      const ordered = [...rules.filter((entry) => entry.above !== undefined || entry.below !== undefined), ...(when === 'otherwise' ? [] : [rule]), ...(when === 'otherwise' ? [rule] : rules.filter((entry) => entry.above === undefined && entry.below === undefined))];
      editor.apply([
        { op: 'set', id: block.id, patch: { tones: { [column]: null } } },
        { op: 'set', id: block.id, patch: { tones: { [column]: ordered } } },
      ]);
    } else set(editor, block, { tones: { [column]: { [at]: tone } } });
    setAt('');
  };
  const ready = Boolean(column) && (numeric ? when === 'otherwise' || (at.trim() !== '' && Number.isFinite(Number(at))) : Boolean(at));
  return (
    <Row keywords={['tones', 'status', 'colour']} label="Value pills" set={entries.length > 0} stacked>
      <Stack spacing={0.75} sx={{ width: '100%' }}>
        {entries.length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {entries.map((entry) => (
              <Chip key={entry.key} label={entry.text} onDelete={editor.pending ? undefined : entry.remove} size="small" sx={CHIP} />
            ))}
          </Stack>
        ) : null}
        <Reveal label="Add a value pill">
          {(close) => (
            <>
              <Stack alignItems="center" direction="row" spacing={0.75}>
                <Picker choices={columns.map((entry) => [entry.name, entry.label])} label="Pill column" onChange={(next) => {
                    setColumn(next);
                    setAt('');
                  }} placeholder="Column…" value={column} />
                {numeric ? (
                  <Picker
                    choices={[
                      ['below', 'below'],
                      ['above', 'above'],
                      ['otherwise', 'otherwise'],
                    ]}
                    label="Pill rule"
                    onChange={setWhen}
                    value={when}
                  />
                ) : null}
              </Stack>
              <Stack alignItems="center" direction="row" spacing={0.75}>
                {column && !numeric ? <Picker choices={members(column).map((member) => [member, memberLabel(member)])} label="Pill value" onChange={setAt} placeholder="Value…" value={at} /> : null}
                {numeric && when !== 'otherwise' ? <TextInput label="Pill threshold" onCommit={setAt} placeholder="0.01" value={at} /> : null}
                <Picker choices={TONES} label="Pill tone" onChange={(next) => setTone(next as Tone)} value={tone} />
                <Button
                  disabled={editor.pending || !ready}
                  onClick={() => {
                    add();
                    close();
                  }}
                  size="small"
                  sx={{ flexShrink: 0, minWidth: 0 }}
                  variant="text"
                >
                  Add
                </Button>
              </Stack>
            </>
          )}
        </Reveal>
      </Stack>
    </Row>
  );
}

export function ColumnGroups({ block, editor, measures }: { block: Block; editor: BoardEditor; measures: Array<EnvelopeColumn> }) {
  const groups = (Array.isArray(block.groups) ? block.groups : []) as Array<Group>;
  const [label, setLabel] = useState('');
  const [picked, setPicked] = useState<Array<string>>([]);
  const labelOf = (name: string) => measures.find((entry) => entry.name === name)?.label ?? humanise(name);
  const grouped = new Set(groups.flatMap((group) => group.columns));
  return (
    <Row keywords={['groups', 'heading']} label="Column groups" set={groups.length > 0} stacked>
      <Stack spacing={0.75} sx={{ width: '100%' }}>
        {groups.length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {groups.map((group, index) => (
              <Chip key={`${group.label}:${index}`} label={`${group.label}: ${group.columns.map(labelOf).join(', ')}`} onDelete={editor.pending ? undefined : () => set(editor, block, { groups: groups.length > 1 ? groups.filter((_, other) => other !== index) : null })} size="small" sx={CHIP} />
            ))}
          </Stack>
        ) : null}
        <Reveal label="Add a column group">
          {(close) => (
            <>
              <TextInput label="Group heading" onCommit={setLabel} placeholder="Group heading, e.g. Sales" value={label} />
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {measures.filter((measure) => !grouped.has(measure.name)).map((measure) => {
                  const on = picked.includes(measure.name);
                  return (
                    <Chip aria-pressed={on} clickable color={on ? 'primary' : 'default'} key={measure.name} label={measure.label} onClick={() => setPicked(on ? picked.filter((name) => name !== measure.name) : [...picked, measure.name])} size="small" sx={{ fontSize: 12.5 }} variant={on ? 'filled' : 'outlined'} />
                  );
                })}
                <Button
                  disabled={editor.pending || !label || !picked.length}
                  onClick={() => {
                    set(editor, block, { groups: [...groups, { label, columns: picked }] });
                    setLabel('');
                    setPicked([]);
                    close();
                  }}
                  size="small"
                  sx={{ minWidth: 0 }}
                  variant="text"
                >
                  Add the group
                </Button>
              </Stack>
            </>
          )}
        </Reveal>
      </Stack>
    </Row>
  );
}
